import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Badge,
    Paper,
    Fab,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { AnimatePresence, motion } from "framer-motion";
import {
    CheckCircleOutlineOutlined as ChoresIcon,
    Settings as SettingsIcon,
    ArrowForward as ArrowIcon,
    AddTask as AddTaskIcon,
    Add as AddIcon,
    Dashboard as DashboardIcon,
    Storefront as ShopIcon,
    ChildCare as ChildIcon,
    DarkMode as DarkIcon,
    LightMode as LightIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Logout as LogoutIcon,
    HourglassEmpty as HourglassIcon,
    SettingsBrightness as SettingsBrightnessIcon,
    Star as StarIcon,
    PaletteOutlined as PaletteIcon,
    Notifications as NotificationsIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useThemeMode } from "../../contexts/ThemeContext";
import { useEffectiveDark } from "../../hooks/useEffectiveDark";
import { useAppSettings } from "../../contexts/AppSettingsContext";
import { useToast } from "../../contexts/ToastContext";
import api from "../../services/api";
import UserAvatar from "./UserAvatar";
import ChildProfile from "./ChildProfile";
import NotificationList from "./NotificationList";
import ApprovalList from "./ApprovalList";
import useNotifications from "../../hooks/useNotifications";
import useBadges from "../../hooks/useBadges";
import { getAccent, getBg, getTextColor, THEMES } from "../../theme/themes";
import { advanceDevDate } from "../../utils/dateUtils";
import TaskFormDrawer from "./TaskFormDrawer";

const hourglassFlip = keyframes`
  0%, 45% { transform: rotate(0deg); }
  50%, 95% { transform: rotate(180deg); }
  100% { transform: rotate(360deg); }
`;

const SIDEBAR_WIDTH = 230;

const PinDot = ({ filled, accent, isDark }) => (
  <Box sx={{
    width: 14, height: 14, borderRadius: '50%',
    bgcolor: filled ? accent : 'transparent',
    border: `2px solid ${filled ? accent : isDark ? '#666' : '#ccc'}`,
    transition: 'background 0.15s',
  }} />
);

const ChildPinDialog = ({ open, onClose, onSuccess, isKiosk, isDark, isAdmin = false }) => {
  const toast = useToast();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/auth/children')
        .then(r => setChildren(r.data))
        .catch(() => {
          toast.error('Lataus epäonnistui');
          setError('Lataus epäonnistui');
        })
        .finally(() => setLoading(false));
    }
  }, [open, toast]);

  // Admin can skip PIN and elevate directly
  const handleAdminElevate = async (child) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/auth/admin-elevate', { childId: child.id });
      onSuccess(res.data.user, res.data.token);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Elevation failed');
      toast.error('Elevation failed');
      setSubmitting(false);
    }
  };

  const handleChildClick = (child) => {
    if (isAdmin) {
      handleAdminElevate(child);
    } else {
      setSelected(child);
      setPin('');
      setError('');
    }
  };

  const handleDigit = async (d) => {
    if (submitting) return;
    const next = pin + d;
    setPin(next);
    setError('');
    if (next.length === 4) {
      setSubmitting(true);
      try {
        const res = await api.post('/auth/pin-login', { userId: selected.id, pin: next });
        if (!isKiosk) {
          localStorage.setItem('auth_token', res.data.token);
          api.setAuthToken(res.data.token);
        }
        onSuccess(res.data.user, res.data.token);
      } catch (err) {
        const errorMsg = err.response?.data?.error || 'Väärä PIN';
        setError(errorMsg);
        toast.error(errorMsg);
        setPin('');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setSelected(null);
    setPin('');
    setError('');
    setSubmitting(false);
    onClose();
  };

  const accent = selected ? getAccent(selected) : getAccent(null);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '24px', p: 1, bgcolor: isDark ? 'background.default' : '#fff' } } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 900, pb: 0, color: isDark ? '#fff' : 'text.primary' }}>
        {selected ? `Hei, ${selected.name}!` : 'Valitse käyttäjä'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>Ladataan...</Box>
        ) : !selected ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', pt: 2 }}>
            {children.map(child => (
              <Box key={child.id} onClick={() => handleChildClick(child)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                  cursor: 'pointer', p: 1.5, borderRadius: '16px', bgcolor: getBg(child, isDark),
                  transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.05)' }, minWidth: 80,
                }}>
                <UserAvatar src={child.avatar_url} frame={child.avatar_frame} size={56} bgcolor={getAccent(child)} sx={{ fontWeight: 900, fontSize: '1.4rem' }}>
                  {child.name.charAt(0)}
                </UserAvatar>
                <Typography variant="body2" fontWeight={700} sx={{ color: getTextColor(child, isDark) }}>{child.name}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
            <Typography variant="body2" color={error ? 'error' : 'text.secondary'} role={error ? 'alert' : undefined}>
              {error || 'Syötä PIN-koodi'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {[0,1,2,3].map(i => <PinDot key={i} filled={i < pin.length} accent={accent} isDark={isDark} />)}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              {[1,2,3,4,5,6,7,8,9].map(d => (
                <Box key={d} onClick={() => handleDigit(String(d))}
                  sx={{
                    width: 60, height: 60, borderRadius: '50%',
                    bgcolor: isDark ? 'background.paper' : '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: '1.3rem', fontWeight: 700,
                    color: isDark ? '#fff' : 'text.primary',
                    '&:active': { bgcolor: `${accent}22` }, userSelect: 'none',
                  }}>
                  {d}
                </Box>
              ))}
              <Box onClick={() => setSelected(null)}
                sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.1rem', color: 'text.secondary', userSelect: 'none', '&:active': { bgcolor: isDark ? 'background.paper' : '#f5f5f5' } }}>
                ←
              </Box>
              <Box onClick={() => handleDigit('0')}
                sx={{
                  width: 60, height: 60, borderRadius: '50%',
                  bgcolor: isDark ? 'background.paper' : '#f5f5f5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: '1.3rem', fontWeight: 700,
                  color: isDark ? '#fff' : 'text.primary',
                  '&:active': { bgcolor: `${accent}22` }, userSelect: 'none',
                }}>
                0
              </Box>
              <Box onClick={() => setPin(p => p.slice(0, -1))}
                sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.1rem', color: 'text.secondary', userSelect: 'none', '&:active': { bgcolor: isDark ? 'background.paper' : '#f5f5f5' } }}>
                ⌫
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Peruuta</Button>
      </DialogActions>
    </Dialog>
  );
};

// Parent login dialog
const ParentLoginDialog = ({ open, onClose, onSuccess }) => {
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [demoHints, setDemoHints] = useState(null);
  const { login } = useAuth();

  // Fetch demo hints when dialog opens
  useEffect(() => {
    if (open) {
      api.getSetupStatus().then(res => {
        const hints = res.data.demoHints || null;
        setDemoHints(hints);
        if (hints?.parentCredentials) {
          const [u, p] = hints.parentCredentials.split(' / ');
          if (u) setUsername(u);
          if (p) setPassword(p);
        }
      }).catch(() => {});
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setSubmitting(true);
    const result = await login(username, password);
    setSubmitting(false);
    if (result.success) {
      onSuccess(result.user);
    } else {
      toast.error(result.error);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '24px', p: 1 } } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 900, pb: 0 }}>
        Vanhemman kirjautuminen
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Käyttäjätunnus"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            autoFocus
            disabled={submitting}
          />
          <TextField
            label="Salasana"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            disabled={submitting}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting || !username || !password}
            sx={{ bgcolor: getAccent(null), '&:hover': { bgcolor: getAccent(null) }, borderRadius: 50, py: 1.5, fontWeight: 700 }}
          >
            {submitting ? 'Kirjaudutaan...' : 'Kirjaudu'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Peruuta</Button>
      </DialogActions>
      {demoHints && (
        <Box sx={{ px: 3, pb: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Demo: {demoHints.parentCredentials} tai {demoHints.kioskCredentials}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

const NAV_PUBLIC = [
    { label: "Päivän tehtävät", icon: <ChoresIcon />, path: "/" },
    { label: "Kauppa", icon: <ShopIcon />, path: "/kauppa" },
    { label: "Uutuudet", icon: <StarIcon />, path: "/uutiset", featureKey: "uutiset" },
    { label: "Profiili", icon: <PersonIcon />, path: "/profiili" },
    { label: "Kalenteri", icon: <CalendarIcon />, path: "/kalenteri" },
];

const NAV_MAIN = [
    { label: "Päivän tehtävät", icon: <ChoresIcon />, path: "/" },
    { label: "Kalenteri", icon: <CalendarIcon />, path: "/kalenteri" },
    { label: "Kauppa", icon: <ShopIcon />, path: "/kauppa" },
    { label: "Uutuudet", icon: <StarIcon />, path: "/uutiset", featureKey: "uutiset" },
    { label: "Profiili", icon: <PersonIcon />, path: "/profiili" },
];

const NAV_ADMIN_BOTTOM = [
    { label: "Hallinta", icon: <DashboardIcon />, path: "/hallinta" },
    {
        label: "Tehtävät",
        icon: <AddTaskIcon />,
        path: "/tehtavat",
        quickAdd: true,
    },
];

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, isKiosk, isKioskContext, isElevated, elevatedChild, elevateChild, deElevateChild, elevationExpiresAt, demoModeEnabled } = useAuth();
    const { mode, preference, setThemePreference } = useThemeMode();
    // eslint-disable-next-line no-unused-vars
    const { iconUpdatedAt, shortName, defaultTheme, features } = useAppSettings();
    const [childLoginOpen, setChildLoginOpen] = useState(false);
    const [parentLoginOpen, setParentLoginOpen] = useState(false);
    const [elevationRemaining, setElevationRemaining] = useState(null);
    const [notifPanelOpen, setNotifPanelOpen] = useState(false);
    const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
    const [taskDrawerEditTask, setTaskDrawerEditTask] = useState(null);

    // Listen for external open-task-drawer events (from TaskManager, etc.)
    useEffect(() => {
        const handler = (e) => {
            setTaskDrawerEditTask(e.detail?.task || null);
            setTaskDrawerOpen(true);
        };
        window.addEventListener('open-task-drawer', handler);
        return () => window.removeEventListener('open-task-drawer', handler);
    }, []);
    const { notifications, pendingApprovals, dismissNotification, clearAllNotifications, handleApprove: approveItem, handleReject: rejectItem, badgeCount } = useNotifications({ user, elevatedChild, isKiosk });
    const { shopBadge, achievementsBadge } = useBadges(user?.id);
    const toast = useToast();

    const isChild = user?.type === 'child';
    const isDark = mode === 'dark';
    const effectiveDark = useEffectiveDark();

    // Get user's premium theme for sidebar styling
    const childTheme = user?.theme
      ? THEMES[user.theme.toLowerCase()]
      : null;
    const activeTextColor = effectiveDark
      ? (childTheme?.surfaces?.primaryText || childTheme?.contrastText || '#fff')
      : (childTheme?.surfaces?.primaryTextLight || childTheme?.contrastText || '#fff');

    // Update elevation countdown
    useEffect(() => {
        if (!elevationExpiresAt) {
            setElevationRemaining(null);
            return;
        }
        const update = () => {
            const remaining = Math.max(0, elevationExpiresAt - Date.now());
            setElevationRemaining(remaining);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [elevationExpiresAt]);

    // Listen for push notifications from SW — handled by useNotifications hook

    // Approvals — handled by useNotifications hook

    const handleApprove = async (item) => {
        try {
            await approveItem(item);
            toast.success('Hyväksytty');
        } catch { toast.error('Hyväksyntä epäonnistui'); }
    };

    const handleReject = async (item) => {
        try {
            await rejectItem(item);
            toast.success('Hylätty');
        } catch { toast.error('Hylkäys epäonnistui'); }
    };

    const formatElevationTime = (ms) => {
        if (!ms || ms <= 0) return null;
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // getAccent reads from module-level appDefaultThemeKey set by AppSettingsContext
    // Destructuring defaultTheme above ensures re-render when it changes
    const accent = childTheme?.main || getAccent(user);

    const navItems = user ? NAV_MAIN : NAV_PUBLIC;
    const isParent = user?.type === 'parent';
    const showAdminItems = isParent && !isKiosk;
    const showChildrenButton = isParent || isKiosk;
    const showSettings = isParent || isKioskContext;

    // Sidebar background — step darker than site bg
    const sidebarBg = effectiveDark ? 'background.sidebar' : 'background.paper';

    return (
        <>
        <Box
            sx={{
                width: SIDEBAR_WIDTH,
                minWidth: SIDEBAR_WIDTH,
                minHeight: "100vh",
                bgcolor: sidebarBg,
                borderRight: `1px solid ${accent}22`,
                display: "flex",
                flexDirection: "column",
                position: "sticky",
                top: 0,
                height: "100vh",
                zIndex: 1200,
                color: effectiveDark ? '#f0f0f0' : 'text.primary',
            }}
        >
            {/* App title */}
            <Box
                sx={{
                    px: 2.5,
                    pt: 3,
                    pb: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                }}
            >
                <Box
                    sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        bgcolor: accent,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 4px 16px ${accent}55`,
                        mb: 0.5,
                        overflow: 'hidden',
                    }}
                >
                    {iconUpdatedAt ? (
                        <Box
                            component="img"
                            src={`/icons/icon-96x96.png?t=${iconUpdatedAt}`}
                            alt="App"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <Typography sx={{ fontSize: "1.6rem" }}>🏆</Typography>
                    )}
                </Box>
                <Typography
                    variant="h6"
                    fontWeight={900}
                    sx={{ color: accent, letterSpacing: "-0.5px" }}
                >
                    {shortName || 'Kotityöt'}
                </Typography>
            </Box>

            <Divider sx={{ borderColor: `${accent}22`, mx: 2 }} />

            {/* Main nav */}
            <List sx={{ flex: 1, pt: 1.5, px: 1 }}>
                {navItems.filter(item => !item.featureKey || features[item.featureKey]).map(({ label, icon, path, quickAdd }) => {
                    const isProfileWithCosmetics = path === '/profiili' && isElevated && elevatedChild && location.search.includes('cosmetics');
                    const active = location.pathname === path && !isProfileWithCosmetics;
                    const hasBadge = (path === '/kauppa' && shopBadge) || (path === '/profiili' && achievementsBadge);
                    return (
                        <ListItem
                            key={path}
                            disablePadding
                            sx={{ mb: 0.5 }}
                            secondaryAction={
                                quickAdd && user ? (
                                    <Tooltip
                                        title="Luo uusi tehtävä"
                                        placement="right"
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTaskDrawerOpen(prev => !prev);
                                            }}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                bgcolor: taskDrawerOpen
                                                    ? `${accent}30`
                                                    : active
                                                        ? "rgba(255,255,255,0.3)"
                                                        : `${accent}18`,
                                                color: active ? activeTextColor : accent,
                                                "&:hover": {
                                                    bgcolor: active
                                                        ? "rgba(255,255,255,0.4)"
                                                        : `${accent}30`,
                                                },
                                            }}
                                        >
                                            <motion.div
                                                animate={{ rotate: taskDrawerOpen ? 45 : 0 }}
                                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <AddIcon sx={{ fontSize: 16 }} />
                                            </motion.div>
                                        </IconButton>
                                    </Tooltip>
                                ) : null
                            }
                        >
                            <ListItemButton
                                onClick={() => { navigate(path); setNotifPanelOpen(false); }}
                                sx={{
                                    borderRadius: 50,
                                    bgcolor: active ? accent : "transparent",
                                    color: active ? activeTextColor : (effectiveDark ? '#f0f0f0' : "text.primary"),
                                    pr: quickAdd && user ? 5 : 2,
                                    "&:hover": {
                                        bgcolor: active
                                            ? accent
                                            : `${accent}15`,
                                    },
                                    "& .MuiListItemIcon-root": {
                                        color: active ? activeTextColor : accent,
                                        minWidth: 36,
                                    },
                                    transition: "background 0.18s ease",
                                }}
                            >
                                <ListItemIcon>
                                    <Box sx={{ position: 'relative', display: 'flex' }}>
                                        {icon}
                                        {hasBadge && !active && (
                                            <Box sx={{
                                                position: 'absolute', top: -2, right: -4,
                                                width: 9, height: 9, borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
                                                boxShadow: '0 2px 6px rgba(255,152,0,0.5)',
                                            }} />
                                        )}
                                    </Box>
                                </ListItemIcon>
                                <ListItemText
                                    primary={label}
                                    primaryTypographyProps={{
                                        fontSize: "0.875rem",
                                        fontWeight: active ? 700 : 500,
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}

                {/* Ulkoasu link for elevated child */}
                {isElevated && elevatedChild && (
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => { navigate('/profiili?tab=cosmetics'); setNotifPanelOpen(false); }}
                        sx={{
                          borderRadius: 50,
                          bgcolor: location.pathname === '/profiili' && location.search.includes('cosmetics') ? accent : 'transparent',
                          color: location.pathname === '/profiili' && location.search.includes('cosmetics') ? activeTextColor : (effectiveDark ? '#f0f0f0' : 'text.primary'),
                          "&:hover": { bgcolor: location.pathname === '/profiili' && location.search.includes('cosmetics') ? accent : `${accent}15` },
                          "& .MuiListItemIcon-root": {
                            color: location.pathname === '/profiili' && location.search.includes('cosmetics') ? activeTextColor : accent,
                            minWidth: 36,
                          },
                          transition: "background 0.18s ease",
                        }}
                      >
                        <ListItemIcon><PaletteIcon /></ListItemIcon>
                        <ListItemText primary="Ulkoasu" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: location.pathname === '/profiili' && location.search.includes('cosmetics') ? 700 : 500 }} />
                      </ListItemButton>
                    </ListItem>
                )}

                {/* Children button - moved above divider for muscle memory */}
                {!isElevated && showChildrenButton && (
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => setChildLoginOpen(true)}
                        sx={{
                          borderRadius: 50,
                          "&:hover": { bgcolor: `${accent}15` },
                          "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                        }}
                      >
                        <ListItemIcon><ChildIcon /></ListItemIcon>
                        <ListItemText primary="Käyttäjät" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }} />
                      </ListItemButton>
                    </ListItem>
                )}
            </List>

            <Divider sx={{ borderColor: `${accent}22`, mx: 2 }} />

            {/* Viestit button */}
            {user && (
                <List sx={{ px: 1, py: 0.5 }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => setNotifPanelOpen(!notifPanelOpen)}
                            sx={{
                                borderRadius: 50,
                                bgcolor: notifPanelOpen ? `${accent}15` : 'transparent',
                                "&:hover": { bgcolor: `${accent}15` },
                                "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                            }}
                        >
                            <ListItemIcon>
                                <Badge
                                    badgeContent={notifications.length + pendingApprovals.length}
                                    color="error"
                                    max={9}
                                    invisible={notifications.length + pendingApprovals.length === 0}
                                >
                                    <NotificationsIcon />
                                </Badge>
                            </ListItemIcon>
                            <ListItemText primary="Viestit" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: notifPanelOpen ? 700 : 500 }} />
                        </ListItemButton>
                    </ListItem>
                </List>
            )}

            <Divider sx={{ borderColor: `${accent}22`, mx: 2 }} />

            {/* Bottom section */}
            <List sx={{ pb: 2, px: 1 }}>
                {isElevated && elevatedChild && (
                    <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChildProfile child={elevatedChild} variant="small" isDark={isDark} showName={false} />
                        <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{elevatedChild.name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <HourglassIcon sx={{ fontSize: 14, color: 'text.secondary', animation: `${hourglassFlip} 3s ease-in-out infinite` }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatElevationTime(elevationRemaining) || 'Kirjautunut'}
                              </Typography>
                            </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => { deElevateChild(); }}
                        sx={{
                          borderRadius: 50,
                          "&:hover": { bgcolor: `${accent}15` },
                          "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                        }}
                      >
                        <ListItemIcon><LogoutIcon /></ListItemIcon>
                        <ListItemText primary="Kirjaudu ulos" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }} />
                      </ListItemButton>
                    </ListItem>
                    <Divider sx={{ borderColor: `${accent}22`, my: 0.5 }} />
                    </>
                )}
                {/* Admin items - Hallinta & Tehtävät for parent users */}
                {!isElevated && showAdminItems && NAV_ADMIN_BOTTOM.map(({ label, icon, path, quickAdd }) => {
                    const active = location.pathname === path;
                    return (
                        <ListItem
                            key={path}
                            disablePadding
                            sx={{ mb: 0.5 }}
                            secondaryAction={
                                quickAdd ? (
                                    <Tooltip title="Luo uusi tehtävä" placement="right">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTaskDrawerOpen(prev => !prev);
                                            }}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                bgcolor: taskDrawerOpen
                                                    ? `${accent}30`
                                                    : active
                                                        ? "rgba(255,255,255,0.3)"
                                                        : `${accent}18`,
                                                color: active ? activeTextColor : accent,
                                                "&:hover": {
                                                    bgcolor: active ? "rgba(255,255,255,0.4)" : `${accent}30`,
                                                },
                                            }}
                                        >
                                            <motion.div
                                                animate={{ rotate: taskDrawerOpen ? 45 : 0 }}
                                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <AddIcon sx={{ fontSize: 16 }} />
                                            </motion.div>
                                        </IconButton>
                                    </Tooltip>
                                ) : null
                            }
                        >
                            <ListItemButton
                                onClick={() => { navigate(path); setNotifPanelOpen(false); }}
                                sx={{
                                    borderRadius: 50,
                                    bgcolor: active ? accent : "transparent",
                                    color: active ? activeTextColor : (effectiveDark ? '#f0f0f0' : "text.primary"),
                                    pr: quickAdd ? 5 : 2,
                                    "&:hover": { bgcolor: active ? accent : `${accent}15` },
                                    "& .MuiListItemIcon-root": { color: active ? activeTextColor : accent, minWidth: 36 },
                                }}
                            >
                                <ListItemIcon>{icon}</ListItemIcon>
                                <ListItemText primary={label} primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: active ? 700 : 500 }} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
                {!isElevated && showSettings && (
                    <ListItem disablePadding sx={{ mt: 0.5 }}>
                        <ListItemButton
                            onClick={() => { navigate("/asetukset"); setNotifPanelOpen(false); }}
                            sx={{
                                borderRadius: 50,
                                bgcolor:
                                    location.pathname === "/asetukset"
                                        ? accent
                                        : "transparent",
                                color:
                                    location.pathname === "/asetukset"
                                        ? activeTextColor
                                        : (effectiveDark ? '#f0f0f0' : "text.primary"),
                                "&:hover": {
                                    bgcolor:
                                        location.pathname === "/asetukset"
                                            ? accent
                                            : `${accent}15`,
                                },
                                "& .MuiListItemIcon-root": {
                                    color:
                                        location.pathname === "/asetukset"
                                            ? activeTextColor
                                            : accent,
                                    minWidth: 36,
                                },
                            }}
                        >
                            <ListItemIcon>
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Asetukset"
                                primaryTypographyProps={{
                                    fontSize: "0.875rem",
                                    fontWeight:
                                        location.pathname === "/asetukset"
                                            ? 700
                                            : 500,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                )}
                {!isElevated && isChild && !isKiosk && (
                    <>
                    <ListItem disablePadding sx={{ mt: 0.5 }}>
                      <ListItemButton
                        onClick={async () => { await logout(); window.location.href = '/'; }}
                        sx={{
                          borderRadius: 50,
                          "&:hover": { bgcolor: `${accent}15` },
                          "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                        }}
                      >
                        <ListItemIcon><LogoutIcon /></ListItemIcon>
                        <ListItemText primary="Kirjaudu ulos" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }} />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding sx={{ mt: 0.5 }}>
                      <ListItemButton
                        onClick={() => { navigate("/asetukset"); setNotifPanelOpen(false); }}
                        sx={{
                          borderRadius: 50,
                          bgcolor: location.pathname === "/asetukset" ? accent : "transparent",
                          color: location.pathname === "/asetukset" ? activeTextColor : (effectiveDark ? '#f0f0f0' : "text.primary"),
                          "&:hover": { bgcolor: location.pathname === "/asetukset" ? accent : `${accent}15` },
                          "& .MuiListItemIcon-root": { color: location.pathname === "/asetukset" ? activeTextColor : accent, minWidth: 36 },
                        }}
                      >
                        <ListItemIcon><SettingsIcon /></ListItemIcon>
                        <ListItemText primary="Asetukset" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: location.pathname === "/asetukset" ? 700 : 500 }} />
                      </ListItemButton>
                    </ListItem>
                    </>
                )}
                {!isElevated && !user && (
                  <>
                    <ListItem disablePadding sx={{ mt: 0.5 }}>
                      <ListItemButton
                        onClick={() => setChildLoginOpen(true)}
                        sx={{
                          borderRadius: 50,
                          "&:hover": { bgcolor: `${accent}15` },
                          "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                        }}
                      >
                        <ListItemIcon><ChildIcon /></ListItemIcon>
                        <ListItemText primary="Käyttäjät" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }} />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding sx={{ mt: 0.5 }}>
                      <ListItemButton
                        onClick={() => setParentLoginOpen(true)}
                        sx={{
                          borderRadius: 50,
                          "&:hover": { bgcolor: `${accent}15` },
                          "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                        }}
                      >
                        <ListItemIcon><ArrowIcon /></ListItemIcon>
                        <ListItemText primary="Vanhemmat" primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }} />
                      </ListItemButton>
                    </ListItem>
                  </>
                )}
                {/* Dark mode toggle - cycles through light → dark → system */}
                <ListItem disablePadding sx={{ mt: 0.5 }}>
                    <ListItemButton
                        onClick={() => {
                            // Cycle: light → dark → system → light
                            if (preference === 'light') setThemePreference('dark');
                            else if (preference === 'dark') setThemePreference('system');
                            else setThemePreference('light');
                        }}
                        sx={{
                            borderRadius: 50,
                            "&:hover": { bgcolor: `${accent}15` },
                            "& .MuiListItemIcon-root": { color: accent, minWidth: 36 },
                        }}
                    >
                        <ListItemIcon>
                            {preference === 'system' ? <SettingsBrightnessIcon /> : mode === 'dark' ? <LightIcon /> : <DarkIcon />}
                        </ListItemIcon>
                        <ListItemText
                            primary={preference === 'system' ? 'Järjestelmä' : preference === 'dark' ? 'Tumma tila' : 'Vaalea tila'}
                            primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>

            {/* Login Dialogs */}
            <ChildPinDialog
              open={childLoginOpen}
              onClose={() => setChildLoginOpen(false)}
              isKiosk={isKiosk}
              isDark={effectiveDark}
              isAdmin={isParent && !isKiosk}
              onSuccess={(childUser, token) => {
                setChildLoginOpen(false);
                if (isKiosk || (isParent && !isKiosk)) {
                  elevateChild(childUser, token);
                } else {
                  window.location.reload();
                }
              }}
            />
            <ParentLoginDialog
              open={parentLoginOpen}
              onClose={() => setParentLoginOpen(false)}
              onSuccess={() => { setParentLoginOpen(false); navigate('/hallinta'); }}
            />
        </Box>

        {/* Click-away backdrop for notification panel */}
        {notifPanelOpen && (
            <Box
                onClick={() => setNotifPanelOpen(false)}
                sx={{ position: 'fixed', inset: 0, zIndex: 1198 }}
            />
        )}

        {/* Notification panel - slides out from sidebar right edge */}
        <AnimatePresence>
            {notifPanelOpen && (
                <motion.div
                    key="notif-panel"
                    initial={{ x: -280 }}
                    animate={{ x: 0 }}
                    exit={{ x: -280 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={{ position: 'fixed', top: 0, left: SIDEBAR_WIDTH, height: '100vh', zIndex: 1199, overflow: 'hidden' }}
                >
                    <Box sx={{
                        width: 280,
                        height: '100%',
                        bgcolor: effectiveDark ? 'background.default' : 'background.paper',
                        borderRight: `1px solid ${accent}22`,
                        borderRadius: '0 12px 12px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <Box sx={{ px: 2, pt: 'calc(env(safe-area-inset-top, 0px) + 16px)', pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" fontWeight={700}>Viestit</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {notifications.length > 0 && (
                                    <Tooltip title="Tyhjennä kaikki">
                                        <IconButton size="small" onClick={clearAllNotifications}>
                                            <DeleteSweepIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <IconButton size="small" onClick={() => setNotifPanelOpen(false)}>
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ borderColor: `${accent}22` }} />

                        {/* Notifications at top */}
                        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 2}}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1.5 }}>
                                    <Typography variant="overline" color="text.secondary" fontWeight={700}>
                                        Ilmoitukset
                                    </Typography>
                                    {notifications.length > 0 && (
                                        <Badge badgeContent={notifications.length} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }} />
                                    )}
                                </Box>
                                <NotificationList notifications={notifications} onDismiss={dismissNotification} accent={accent} />
                            </Box>

                            {/* Pending approvals pushed to bottom */}
                            {showAdminItems && pendingApprovals.length > 0 && (
                                <Box sx={{ mt: 'auto', pt: 1 }}>
                                    <Divider sx={{ mb: 1 }} />
                                    <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ px: 0.5 }}>
                                        Hyväksynnät ({pendingApprovals.length})
                                    </Typography>
                                    <ApprovalList
                                        approvals={pendingApprovals}
                                        onApprove={handleApprove}
                                        onReject={handleReject}
                                        maxItems={8}
                                        onShowMore={() => { setNotifPanelOpen(false); navigate('/hallinta'); }}
                                        accent={accent}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>

        {/* Demo mode: floating +1 day button */}
        {demoModeEnabled && (
            <Fab
                onClick={() => { advanceDevDate(1); window.location.reload(); }}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    bgcolor: '#000',
                    border: '2px solid #d32f2f',
                    color: '#FFD600',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    width: 56,
                    height: 56,
                    zIndex: 1300,
                    '&:hover': { bgcolor: '#1a1a1a' },
                    flexDirection: 'column',
                    gap: 0,
                }}
            >
                <CalendarIcon sx={{ fontSize: 18, color: '#FFD600' }} />
                <span style={{ fontSize: '0.6rem', lineHeight: 1, color: '#FFD600', fontWeight: 700 }}>+1pv</span>
            </Fab>
        )}
        <TaskFormDrawer open={taskDrawerOpen} onClose={() => { setTaskDrawerOpen(false); setTaskDrawerEditTask(null); }} onSuccess={() => window.dispatchEvent(new Event('task-drawer-success'))} editTask={taskDrawerEditTask} anchor="left" />
        </>
    );
};

export default Sidebar;
